import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { PUBLIC_CATEGORIES } from "./categories";

export function PageHero({ badge, title, subtitle, children }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 hero-grid opacity-30" aria-hidden="true" />
      <div className="absolute -top-24 end-0 w-[420px] h-[420px] bg-brand/20 rounded-full blur-[120px]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-5 pt-20 pb-14 animate-fadeup">
        {badge && <p className="inline-flex px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-xs font-mono uppercase tracking-widest text-brand mb-6">{badge}</p>}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-black leading-[1.05] tracking-tight max-w-3xl">{title}</h1>
        {subtitle && <p className="text-slate-300 text-base md:text-lg mt-6 max-w-2xl">{subtitle}</p>}
        {children}
      </div>
    </section>
  );
}

export function CategoryGrid({ detailed = false }) {
  const { t } = useTranslation();
  return (
    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {PUBLIC_CATEGORIES.map((c, i) => (
        <li key={c.key} data-testid={`category-card-${c.key}`} style={{ animationDelay: `${i * 0.06}s` }}
          className="animate-fadeup group rounded-2xl border border-white/10 bg-white/[0.04] p-6 hover:bg-white/[0.08] hover:border-brand/40 transition-colors">
          <span className="w-12 h-12 rounded-xl bg-brand/15 text-brand flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"><c.icon className="w-6 h-6" aria-hidden="true" /></span>
          <h3 className="font-display font-bold text-lg">{t(`site.cats.${c.key}.name`)}</h3>
          <p className="text-sm text-slate-400 mt-2">{t(`site.cats.${c.key}.desc`)}</p>
          {detailed && <p className="text-xs font-mono text-slate-500 mt-4">{t(`site.cats.${c.key}.examples`)}</p>}
        </li>
      ))}
    </ul>
  );
}

export function CtaBand({ title, body, to, cta, testId }) {
  return (
    <section className="max-w-7xl mx-auto px-5 pb-20">
      <div className="rounded-3xl bg-brand p-8 sm:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-display font-black text-white">{title}</h2>
          <p className="text-white/85 mt-2 max-w-xl">{body}</p>
        </div>
        <Link to={to} data-testid={testId}>
          <Button size="lg" className="rounded-full h-12 px-7 bg-slate-950 hover:bg-slate-800 text-white gap-2">{cta} <ArrowRight className="w-4 h-4 rtl:rotate-180" aria-hidden="true" /></Button>
        </Link>
      </div>
    </section>
  );
}

export function SectionHead({ title, subtitle }) {
  return (
    <div className="mb-10 max-w-2xl">
      <h2 className="text-2xl sm:text-3xl font-display font-bold">{title}</h2>
      {subtitle && <p className="text-slate-400 mt-2">{subtitle}</p>}
    </div>
  );
}

export function StepList({ steps }) {
  return (
    <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {steps.map((s, i) => (
        <li key={s.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6" data-testid={`step-${i + 1}`}>
          <span className="font-mono text-brand text-sm">0{i + 1}</span>
          <s.icon className="w-6 h-6 text-white mt-4" aria-hidden="true" />
          <h3 className="font-display font-bold text-lg mt-3">{s.title}</h3>
          <p className="text-sm text-slate-400 mt-2">{s.body}</p>
        </li>
      ))}
    </ol>
  );
}
